import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepConnector from '@material-ui/core/StepConnector';
import StepLabel from '@material-ui/core/StepLabel';

import UserDetailsStep from './UserDetailsStep';
import CreateUserStep from './CreateUserStep';
import { isValidEmail } from '../../../../utils/validators';

const styles = theme => ({
    root: {
        flexGrow: 1,

        paddingTop: theme.spacing(),
    },
    button: {
        marginRight: theme.spacing(2),

        '&:last-child': {
            marginRight: 0,
        },
    },
    actions: {
        marginTop: 10,
        float: 'right',
    },
    paper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: theme.spacing(8),
        paddingBottom: theme.spacing(8),
    },
    connector: { flex: 0.1 },
    horizontal: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    form: {
        width: 760,
        maxWidth: 760,
        padding: theme.spacing(2),
    },
});

const getStepContent = (
    step,
    userDetails,
    userDetailsErrors,
    canEdit,
    additionalData,
    onChange,
    editable
) => {
    switch (step) {
        case 0:
            return (
                <div style={{ height: 'auto' }}>
                    <UserDetailsStep
                        editable={editable}
                        userDetails={userDetails}
                        userDetailsErrors={userDetailsErrors}
                        additionalData={additionalData}
                        onChange={onChange}
                        canEdit={canEdit}
                    />
                </div>
            );
        case 1:
            return (
                <div style={{ height: 600 }}>
                    <CreateUserStep
                        userDetails={userDetails}
                        userDetailsErrors={userDetailsErrors}
                        companyGroups={additionalData.companyGroups}
                        onChange={onChange}
                    />
                </div>
            );
        default:
            return 'Ups';
    }
};

const getInitialUserState = currentUser => {
    if (currentUser) {
        return {
            uuid: currentUser.uuid,
            partner_portal_username:
                currentUser.partner_portal_username || currentUser.email || '',
            email: currentUser.email,
            roles: (currentUser.roles || []).map(({ uuid }) => uuid),
            company_groups: (currentUser.company_groups || []).map(
                ({ uuid }) => uuid
            ),
        };
    }
    return {
        uuid: '',
        partner_portal_username: '',
        email: '',
        roles: [],
        company_groups: [],
    };
};

const getInitialEditState = currentUser => {
    if (currentUser) {
        return {
            partner_portal_username: false,
            email: false,
            roles: true,
        };
    }
    return {
        email: true,
        partner_portal_username: true,
        roles: true,
    };
};

const validateUserDetails = (user, additionalData, includeGroups = false) => {
    const { partner_portal_username } = user;

    let errorFields = {
        partner_portal_username: !Boolean(partner_portal_username),
        email: !isValidEmail(user.email),
        roles: !Boolean(user.roles),
    };

    const foundUser = additionalData.companyUsers?.find(
        u => u.email === user.email
    );

    if (
        !errorFields.partner_portal_username &&
        foundUser &&
        foundUser.partner_portal_username !== partner_portal_username &&
        !isValidEmail(partner_portal_username)
    ) {
        errorFields.partner_portal_username =
            'Username must match email or legacy name';
    }

    if (includeGroups) {
        errorFields.company_groups = !(
            Boolean(user.company_groups) && user.company_groups.length > 0
        );
    }

    const valid = Object.values(errorFields).reduce((acc, val) => {
        if (acc) {
            return !Boolean(val);
        }
        return acc;
    }, true);

    return [valid, errorFields];
};

const getSteps = () => {
    return ['User details', 'Create user'];
};

let UserForm = props => {
    const {
        classes,
        editable,
        currentUser,
        additionalData,
        serverSideErrors,
        onSave,
        companyId,
    } = props;
    const [activeStep, setActiveStep] = useState(0);
    const [canEdit, setCanEdit] = useState(getInitialEditState(currentUser));
    const [userDetails, setUserDetails] = useState(
        getInitialUserState(currentUser)
    );

    const [userDetailsErrors, setUserDetailsErrors] = useState({});

    useEffect(() => setUserDetailsErrors(serverSideErrors), [serverSideErrors]);
    const steps = getSteps();

    const handleNext = () => {
        const [valid, errors] = validateUserDetails(
            userDetails,
            additionalData
        );
        setUserDetailsErrors(errors);
        if (valid) {
            setActiveStep(prevActiveStep => prevActiveStep + 1);
        }
    };
    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1);
    };

    const handleSave = () => {
        const [valid, errors] = validateUserDetails(
            userDetails,
            additionalData,
            true
        );
        setUserDetailsErrors(errors);
        if (valid) {
            onSave(userDetails);
        }
    };

    const onEmailChange = email => {
        const user = additionalData.portalUsers.find(u => u.email === email);
        setUserDetailsErrors({ partner_portal_access: false });
        if (user) {
            if (user.partner_portal_access) {
                setUserDetailsErrors({ partner_portal_access: true });
                setUserDetails(getInitialUserState());
            } else {
                const { partner_portal_access, ...rest } = user;
                setUserDetails(getInitialUserState(rest));
            }
        } else {
            setUserDetails({
                ...getInitialUserState(),
                email: email,
                partner_portal_username: email,
            });
            setCanEdit(getInitialEditState());
        }
    };

    const onChange = (prop, val) => {
        setUserDetails({ ...userDetails, [prop]: val });
        setUserDetailsErrors({ ...userDetailsErrors, [prop]: false });
        if (prop === 'email') {
            onEmailChange(val);
        } else if (prop === 'partner_portal_username') {
            const currentEmail = userDetails.email;
            const validEmail = isValidEmail(val);
            const matchEmail = validEmail && currentEmail === val;

            setUserDetailsErrors({
                ...userDetailsErrors,
                partner_portal_username: matchEmail
                    ? false
                    : 'Selected user email and username must match.',
                partner_portal_username_taken: false,
            });
        }
    };

    return (
        <div className={classes.root}>
            <Stepper
                activeStep={activeStep}
                classes={{
                    horizontal: classes.horizontal,
                }}
                connector={<StepConnector className={classes.connector} />}
            >
                {steps.map(label => {
                    return (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            <div className={classes.paper}>
                <Paper className={classes.form} elevation={1}>
                    {getStepContent(
                        activeStep,
                        userDetails,
                        userDetailsErrors,
                        canEdit,
                        additionalData,
                        onChange,
                        editable
                    )}
                    {activeStep === steps.length - 1 ? (
                        <div className={classes.actions}>
                            <Button
                                onClick={handleBack}
                                className={classes.button}
                                color="primary"
                            >
                                Back
                            </Button>
                            <Button
                                color="secondary"
                                onClick={handleSave}
                                variant="contained"
                                className={classes.button}
                            >
                                Save
                            </Button>
                        </div>
                    ) : (
                        <div className={classes.actions}>
                            <Button
                                onClick={handleBack}
                                className={classes.button}
                                color="primary"
                                component={Link}
                                to={`/companies/${companyId}/partner/access`}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="secondary"
                                onClick={handleNext}
                                variant="contained"
                                className={classes.button}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </Paper>
            </div>
        </div>
    );
};

export default withStyles(styles)(UserForm);
